import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical } from "lucide-react";

const COLUMNS = [
  { key: "pending", label: "Pendiente", color: "#64748B" },
  { key: "design", label: "Diseño", color: "#6366F1" },
  { key: "production", label: "Producción", color: "#F59E0B" },
  { key: "ready", label: "Listo", color: "#0047AB" },
  { key: "delivered", label: "Entregado", color: "#10B981" },
];

export default function AdminKanban() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = () => {
    API.get("/orders").then((r) => { setOrders(r.data); setLoading(false); }).catch(() => { setOrders([]); setLoading(false); });
  };

  useEffect(() => { fetchOrders(); }, []);

  const moveOrder = async (orderId, newStatus) => {
    await API.put(`/admin/orders/${orderId}/status`, { status: newStatus });
    fetchOrders();
  };

  const getColumnOrders = (status) => orders.filter((o) => o.status === status);

  return (
    <div className="p-6 h-full" data-testid="admin-kanban-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Producción</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Tablero Kanban</h1>
      </div>

      {loading ? (
        <p className="text-sm text-[#94A3B8]">Cargando...</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {COLUMNS.map((col) => (
            <div key={col.key} className="kanban-column flex-shrink-0" data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                <span className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>{col.label}</span>
                <span className="text-xs text-[#94A3B8] ml-auto">{getColumnOrders(col.key).length}</span>
              </div>
              <div className="space-y-2 min-h-[200px]">
                {getColumnOrders(col.key).map((order) => (
                  <div key={order.id} className="bg-white border border-[#E2E8F0] rounded-sm p-3 hover:border-[#0047AB] transition-colors" data-testid={`kanban-card-${order.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-xs font-bold text-[#0F172A]">{order.client_name}</p>
                        <p className="text-[10px] text-[#475569] mt-0.5">{order.items?.map((i) => i.product_name).join(", ")}</p>
                      </div>
                      <GripVertical className="w-3 h-3 text-[#CBD5E1]" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-[#475569]">
                        <span>{order.items?.[0]?.quantity} pzas</span>
                        <span className="mx-1">·</span>
                        <span>{order.items?.[0]?.technique_name}</span>
                      </div>
                      <span className="text-xs font-bold text-[#0F172A]">Q{order.total}</span>
                    </div>
                    {order.delivery_date && (
                      <p className="text-[10px] text-[#94A3B8] mt-1">Entrega: {order.delivery_date}</p>
                    )}
                    <div className="mt-2 pt-2 border-t border-[#E2E8F0]">
                      <Select value={order.status} onValueChange={(val) => moveOrder(order.id, val)}>
                        <SelectTrigger className="h-7 text-[10px] border-[#CBD5E1] rounded-sm" data-testid={`status-select-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COLUMNS.map((c) => (
                            <SelectItem key={c.key} value={c.key} className="text-xs">{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {getColumnOrders(col.key).length === 0 && (
                  <div className="border border-dashed border-[#E2E8F0] rounded-sm p-4 text-center text-xs text-[#94A3B8]">
                    Sin pedidos
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
